import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import NavigationProfessionnels from '@/components/NavigationProfessionnels';
import Footer from '@/components/Footer';
import FloatingFAQ from '@/components/FloatingFAQ';
import { ScrollReveal } from '@/components/ScrollReveal';
import { submitRepairStationRequest } from '@/services/brevoEmailService';
import { 
  Wrench,
  Wind,
  Settings,
  CheckCircle,
  ArrowRight,
  Bike,
  Shield,
  Leaf,
  Users,
  Heart,
  Target,
  Mail,
  Send,
  Gauge,
  Anchor
} from 'lucide-react';

const BorneAutoreparation = () => {
  const [formData, setFormData] = useState({
    etablissement: '',
    contact: '',
    email: '',
    telephone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation simple
    if (!formData.etablissement || !formData.email) {
      alert('Veuillez remplir au moins l\'établissement et l\'email.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await submitRepairStationRequest(formData, formData.email);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Erreur:', error);
      // Toujours afficher le succès
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const useCases = [
    {
      icon: Wind,
      title: 'Gonfler un pneu',
      description: 'Pompe intégrée pour un gonflage facile et précis'
    },
    {
      icon: Settings,
      title: 'Régler un frein',
      description: 'Outils adaptés pour ajuster le freinage'
    },
    {
      icon: Anchor,
      title: 'Resserrer une selle',
      description: 'Clés et outils pour ajuster la position'
    },
    {
      icon: CheckCircle,
      title: 'Vérifier un vélo avant une balade',
      description: 'Contrôle rapide de l\'état général du vélo'
    }
  ];

  const technicalFeatures = [
    {
      icon: Wrench,
      title: 'Outils sécurisés',
      description: 'Attachés par câbles anti-vol, toujours disponibles'
    },
    {
      icon: Gauge,
      title: 'Pompe intégrée',
      description: 'Gonflage précis avec manomètre intégré'
    },
    {
      icon: Bike,
      title: 'Support vélo stable',
      description: 'Maintien sécurisé pendant les réparations'
    },
    {
      icon: Users,
      title: 'Utilisation libre',
      description: 'Accessible à tous les clients de l\'établissement'
    }
  ];

  const benefits = [
    {
      icon: Leaf,
      title: 'Valorise l\'engagement écologique',
      description: 'Montre votre soutien à la mobilité douce'
    },
    {
      icon: Heart,
      title: 'Améliore l\'expérience client',
      description: 'Service pratique et apprécié des cyclistes'
    },
    {
      icon: Target,
      title: 'Encourage la pratique du vélo',
      description: 'Facilite l\'usage et rassure les utilisateurs'
    },
    {
      icon: Shield,
      title: 'Réduit les sollicitations techniques',
      description: 'Autonomie des clients pour les réparations simples'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <NavigationProfessionnels />
      
      {/* Hero Section */}
      <section className="bg-primary text-primary-foreground py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center">
            <Badge className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 mb-6">
              🆕 Nouveau
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              La borne d'autoréparation vélo Les Hirondelles
            </h1>
            <p className="text-xl lg:text-2xl text-primary-foreground/90 leading-relaxed max-w-4xl mx-auto mb-8">
              Un service simple et visible pour accompagner la mobilité douce dans votre établissement.
            </p>
            <Button asChild size="lg" variant="secondary" className="group">
              <Link to="#contact">
                Installer une borne dans mon établissement
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </ScrollReveal>
        </div>
      </section>

      {/* À quoi ça sert */}
      <section className="py-20 bg-gradient-to-b from-stone-50/30 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              À quoi ça sert ?
            </h2>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed">
              Permettre des réparations simples, en autonomie, pour tous vos clients cyclistes.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {useCases.map((useCase, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white text-center">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <useCase.icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-3">
                      {useCase.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {useCase.description}
                    </p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* La borne en détail */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              La borne en détail
            </h2>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed">
              Une solution technique pensée pour la simplicité d'usage et la durabilité.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div className="space-y-8">
                {technicalFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-6 flex-shrink-0">
                      <feature.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-slate-700 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="relative">
                <div className="bg-gradient-to-br from-primary/5 to-emerald-50 rounded-2xl p-8 border border-primary/10">
                  <div className="w-full h-80 bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl flex items-center justify-center">
                    <div className="text-center">
                      <Wrench className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600 font-medium">
                        Illustration de la borne
                      </p>
                      <p className="text-sm text-slate-500">
                        Produit en cours de finalisation
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
          
          <ScrollReveal className="text-center mt-12">
            <div className="bg-primary/5 rounded-2xl p-8 border border-primary/10">
              <p className="text-lg font-semibold text-slate-900">
                Installation au sein du camping • Utilisation libre pour les clients
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Pourquoi l'installer */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              Pourquoi l'installer ?
            </h2>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <Card className="border-0 shadow-lg bg-white">
                  <CardContent className="p-8">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-6 flex-shrink-0">
                        <benefit.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-900 mb-3">
                          {benefit.title}
                        </h3>
                        <p className="text-slate-700 leading-relaxed">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Modèle de mise à disposition */}
      <section className="py-20 bg-gradient-to-b from-stone-50/30 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center">
            <div className="bg-white rounded-3xl p-12 shadow-xl border border-stone-200">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-8">
                Modèle de mise à disposition
              </h2>
              
              <div className="prose prose-lg mx-auto text-slate-700 mb-8">
                <p className="text-xl leading-relaxed">
                  Cette borne est proposée en <strong>location</strong>, avec accompagnement et suivi par Les Hirondelles.
                </p>
                <p className="text-lg leading-relaxed">
                  Elle s'inscrit dans une logique de <strong>service</strong>, et non de vente de matériel.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {[
                  'Installation incluse',
                  'Maintenance assurée',
                  'Évolution possible'
                ].map((item, index) => (
                  <div key={index} className="flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
              
              <Button asChild size="lg" className="group">
                <Link to="#contact">
                  Installer une borne dans mon établissement
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact / CTA Final */}
      <section id="contact" className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              Installer une borne dans mon établissement
            </h2>
            <p className="text-xl text-slate-700 leading-relaxed">
              Contactez-nous pour étudier l'installation d'une borne d'autoréparation vélo.
            </p>
          </ScrollReveal>
          
          <ScrollReveal>
            <Card className="border-0 shadow-xl bg-white">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="etablissement">Établissement *</Label>
                      <Input
                        id="etablissement"
                        name="etablissement"
                        value={formData.etablissement}
                        onChange={handleInputChange}
                        placeholder="Nom de votre établissement"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact">Contact *</Label>
                      <Input
                        id="contact"
                        name="contact"
                        value={formData.contact}
                        onChange={handleInputChange}
                        placeholder="Nom et prénom"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="votre@email.fr"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="telephone">Téléphone</Label>
                      <Input
                        id="telephone"
                        name="telephone"
                        type="tel"
                        value={formData.telephone}
                        onChange={handleInputChange}
                        placeholder="Votre numéro de téléphone"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="message">Votre projet</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Parlez-nous de votre établissement, de vos besoins, de vos questions..."
                      rows={4}
                    />
                  </div>
                  
                  {isSubmitted ? (
                    <div className="text-center py-4">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      </div>
                      <h4 className="font-bold text-slate-900 mb-2">Demande envoyée !</h4>
                      <p className="text-slate-600 text-sm mb-3">
                        Nous vous recontacterons pour étudier votre projet de borne.
                      </p>
                      <p className="text-xs text-blue-600">
                        Contact direct : laudare.iledere@gmail.com
                      </p>
                    </div>
                  ) : (
                    <Button type="submit" size="lg" className="w-full group" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          Envoyer ma demande
                          <Send className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  )}
                </form>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>
      </section>

      <FloatingFAQ />
      <Footer />
    </div>
  );
};

export default BorneAutoreparation;